import { z } from 'zod'
import { oc } from '@orpc/contract'
import { pageSchema } from '../../utils/page.contract'
import { recordQuerySchema } from '../../utils/record-query.contract'
import {
  CPG_MEASURE_KIND_VALUES,
  CPG_MEASURE_PROPOSAL_STATUS_VALUES,
  CPG_PROPOSAL_CLUSTER_STATUS_VALUES,
  cpgMeasureCitationSchema,
  cpgMeasureSchema,
} from './cpg-registry.contract'

/**
 * Measure proposals and their clusters (PD-1716, docs/cpg/CPG_ADMIN.md "Proposals"). The
 * Risks & Gaps reports accumulate proposals (PD-1714); the cpg.proposals.cluster task groups
 * them; an admin accepts a cluster (a new measure, at least one guideline citation) or
 * dismisses it. There is no per-proposal decision — a proposal is decided with its cluster.
 */

/** Filterable/sortable columns of the proposals table. */
export const CPG_PROPOSAL_QUERY_FIELDS = [
  'id',
  'label',
  'slug',
  'kind',
  'familySlug',
  'status',
  'clusterId',
  'generatedAt',
] as const

export const cpgMeasureProposalSchema = z.object({
  id: z.number(),
  /** The proposing report's DocumentReference id. */
  reportId: z.string(),
  label: z.string(),
  slug: z.string(),
  kind: z.enum(CPG_MEASURE_KIND_VALUES),
  /** Null when the proposing agent named an unknown family. */
  familyId: z.number().nullable(),
  familySlug: z.string().nullable(),
  familyLabel: z.string().nullable(),
  definition: z.string(),
  gradingRule: z.string(),
  rationale: z.string(),
  /** cpg_unit ids the agent grounded the proposal in. */
  guidelineUnitIds: z.array(z.number()),
  status: z.enum(CPG_MEASURE_PROPOSAL_STATUS_VALUES),
  clusterId: z.number().nullable(),
  measureId: z.number().nullable(),
  /** The proposing report's date. */
  generatedAt: z.iso.datetime(),
})
export type CpgMeasureProposal = z.infer<typeof cpgMeasureProposalSchema>

const measureRefSchema = z.object({ id: z.number(), slug: z.string(), label: z.string() })

export const cpgProposalClusterSchema = z.object({
  id: z.number(),
  taskRunId: z.number(),
  label: z.string(),
  /** Suggested measure slug — editable when accepting. */
  slug: z.string(),
  kind: z.enum(CPG_MEASURE_KIND_VALUES),
  familyId: z.number(),
  familySlug: z.string(),
  familyLabel: z.string(),
  definition: z.string(),
  gradingRule: z.string(),
  rationale: z.string(),
  distinctPatients: z.number(),
  proposalCount: z.number(),
  /** Earliest and latest proposing report among the members; null with no members. */
  firstSeenAt: z.iso.datetime().nullable(),
  lastSeenAt: z.iso.datetime().nullable(),
  status: z.enum(CPG_PROPOSAL_CLUSTER_STATUS_VALUES),
  /** An active measure that may already cover the cluster — review its wording first. */
  relatedMeasure: measureRefSchema.nullable(),
  relatedReason: z.string().nullable(),
  /** The dismissed cluster this one recurs from. */
  previousCluster: z
    .object({ id: z.number(), label: z.string(), decidedAt: z.iso.datetime().nullable() })
    .nullable(),
  /** The measure created by accepting it. */
  measure: measureRefSchema.nullable(),
  /**
   * The union of the members' guideline passages — the citations offered when accepting.
   * Empty = the cluster needs a guideline source before it can be accepted.
   */
  suggestedCitations: z.array(cpgMeasureCitationSchema),
  createdAt: z.iso.datetime(),
  decidedAt: z.iso.datetime().nullable(),
  decidedBy: z.string().nullable(),
})
export type CpgProposalCluster = z.infer<typeof cpgProposalClusterSchema>

/** Every proposal, any status; newest first by default. */
export const listCpgProposals = oc
  .route({ method: 'POST', path: '/v2/cpg/proposals/list' })
  .input(recordQuerySchema(CPG_PROPOSAL_QUERY_FIELDS))
  .output(z.object({ page: pageSchema, data: z.array(cpgMeasureProposalSchema) }))

/**
 * Clusters by state — `open` (recommended / below-threshold, the current run's output),
 * `accepted` or `dismissed`. Unpaged: open clusters are bounded by the pending pool, decided
 * ones by admin decisions. Ordered by family, then distinct patients (most first).
 */
export const listCpgProposalClusters = oc
  .route({ method: 'GET', path: '/v2/cpg/proposal-clusters' })
  .input(z.object({ state: z.enum(['open', 'accepted', 'dismissed']).default('open') }))
  .output(z.object({ data: z.array(cpgProposalClusterSchema) }))

/**
 * Accept an open cluster: create an active measure (`origin = 'clustered'`, the cluster's
 * rationale) from the edited texts, cite at least one guideline passage, and mark the member
 * proposals accepted. The measure's kind is the cluster's. A cluster no longer open is
 * CONFLICT (decided, or replaced by a newer clustering run); so is a duplicate slug or an
 * inactive family, as for createCpgMeasure.
 */
export const acceptCpgProposalCluster = oc
  .route({ method: 'POST', path: '/v2/cpg/proposal-clusters/{id}/accept' })
  .input(
    z.object({
      id: z.coerce.number().int().positive(),
      familyId: z.number().int().positive(),
      slug: z
        .string()
        .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'slug must be kebab-case (a-z, 0-9, dashes)'),
      label: z.string().min(1),
      definition: z.string().min(1),
      gradingRule: z.string().min(1),
      /** cpg_unit ids to cite — at least one: nothing enters the registry without a guideline. */
      unitIds: z.array(z.number().int().positive()).min(1, 'Cite at least one guideline passage'),
    }),
  )
  .output(z.object({ data: cpgProposalClusterSchema, measure: cpgMeasureSchema }))

/**
 * Dismiss an open cluster: it and its member proposals are `dismissed` and leave the pool for
 * good. Proposals arriving later are clustered afresh and must reach the threshold on their
 * own; the clusterer may point such a cluster back at this one.
 */
export const dismissCpgProposalCluster = oc
  .route({ method: 'POST', path: '/v2/cpg/proposal-clusters/{id}/dismiss' })
  .input(z.object({ id: z.coerce.number().int().positive() }))
  .output(z.object({ data: cpgProposalClusterSchema }))
